'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/local-api/client';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';
import {
  UserGroupIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  LockClosedIcon,
  GlobeAltIcon,
  UsersIcon,
  XMarkIcon,
  CheckIcon,
  ChevronRightIcon,
  PhotoIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';

interface Community {
  id: string;
  name: string;
  description: string;
  category: string;
  creator_id: string;
  member_count: number;
  is_private: boolean;
  created_at: string;
  profile_photo_url?: string | null;
  cover_photo_url?: string | null;
}

interface MembershipMap {
  [communityId: string]: boolean;
}

// Map of communityId -> latest activity ISO timestamp
interface ActivityMap {
  [communityId: string]: string | null;
}

const CATEGORIES = ['All', 'General', 'Safety', 'Research', 'Trading', 'Education', 'Community', 'DeFi', 'Forex'];

const CATEGORY_COLORS: Record<string, string> = {
  General: 'bg-slate-500/20 text-slate-400',
  Safety: 'bg-red-500/20 text-red-400',
  Research: 'bg-blue-500/20 text-blue-400',
  Trading: 'bg-green-500/20 text-green-400',
  Education: 'bg-yellow-500/20 text-yellow-400',
  Community: 'bg-purple-500/20 text-purple-400',
  DeFi: 'bg-cyan-500/20 text-cyan-400',
  Forex: 'bg-orange-500/20 text-orange-400',
};

const LAST_VISITED_KEY = 'group_last_visited';

function getLastVisited(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem(LAST_VISITED_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function setLastVisited(communityId: string) {
  if (typeof window === 'undefined') return;
  const map = getLastVisited();
  map[communityId] = new Date().toISOString();
  localStorage.setItem(LAST_VISITED_KEY, JSON.stringify(map));
}

async function uploadGroupPhoto(file: File, communityId: string, type: 'profile' | 'cover'): Promise<string | null> {
  const supabase = createClient();
  const ext = file.name.split('.').pop();
  const path = `groups/${communityId}/${type}-${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from('community-media').upload(path, file, { upsert: true });
  if (error) return null;
  const { data } = supabase.storage.from('community-media').getPublicUrl(path);
  return data.publicUrl;
}

function CreateCommunityModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('General');
  const [isPrivate, setIsPrivate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState<string | null>(null);
  const profilePhotoRef = useRef<HTMLInputElement>(null);

  const handleProfilePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProfilePhoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setProfilePhotoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!user) { toast.error('Please sign in to create a community'); return; }
    if (!name.trim()) { toast.error('Community name is required'); return; }
    setSubmitting(true);
    const supabase = createClient();
    const { data, error } = await supabase
      .from('communities')
      .insert({
        name: name.trim(),
        description: description.trim() || null,
        category,
        creator_id: user.id,
        is_private: isPrivate,
        member_count: 1,
      })
      .select()
      .single();

    if (error) {
      toast.error('Failed to create community');
      setSubmitting(false);
      return;
    }

    if (profilePhoto && data?.id) {
      const url = await uploadGroupPhoto(profilePhoto, data.id, 'profile');
      if (url) {
        await supabase.from('communities').update({ profile_photo_url: url }).eq('id', data.id);
      }
    }

    await supabase.from('community_members').insert({
      community_id: data.id,
      user_id: user.id,
      role: 'admin',
    });

    toast.success('Community created successfully!');
    onCreated();
    onClose();
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <div className="w-9 h-9 rounded-xl bg-primary/20 flex items-center justify-center">
            <UserGroupIcon className="text-primary" style={{ width: 18, height: 18 }} />
          </div>
          <h2 className="text-base font-bold text-foreground flex-1">Create Community</h2>
          <button onClick={onClose} className="w-7 h-7 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center text-muted-foreground transition-colors">
            <XMarkIcon style={{ width: 14, height: 14 }} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Group Profile Photo */}
          <div>
            <label className="block text-xs font-semibold text-foreground mb-2">Group Profile Photo</label>
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 rounded-2xl border-2 border-dashed border-border flex items-center justify-center overflow-hidden cursor-pointer hover:border-primary/50 transition-colors flex-shrink-0"
                onClick={() => profilePhotoRef.current?.click()}
              >
                {profilePhotoPreview ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={profilePhotoPreview} alt="Group profile preview" className="w-full h-full object-cover" />
                ) : (
                  <PhotoIcon className="text-muted-foreground" style={{ width: 24, height: 24 }} />
                )}
              </div>
              <div className="flex-1">
                <button
                  type="button"
                  onClick={() => profilePhotoRef.current?.click()}
                  className="text-xs font-semibold text-primary hover:underline"
                >
                  {profilePhotoPreview ? 'Change photo' : 'Upload photo'}
                </button>
                <p className="text-[11px] text-muted-foreground mt-0.5">JPG, PNG or WebP. Max 5MB</p>
              </div>
              <input
                ref={profilePhotoRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                className="hidden"
                onChange={handleProfilePhotoChange}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Community Name <span className="text-red-500">*</span></label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. DeFi Traders Hub"
              maxLength={60}
              className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this community about?"
              rows={3}
              maxLength={300}
              className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            >
              {CATEGORIES.filter((c) => c !== 'All').map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div
            className="flex items-center justify-between p-3 rounded-xl border border-border bg-muted/30 cursor-pointer"
            onClick={() => setIsPrivate(!isPrivate)}
          >
            <div className="flex items-center gap-2.5">
              {isPrivate ? (
                <LockClosedIcon className="text-amber-400" style={{ width: 16, height: 16 }} />
              ) : (
                <GlobeAltIcon className="text-green-400" style={{ width: 16, height: 16 }} />
              )}
              <div>
                <p className="text-sm font-semibold text-foreground">{isPrivate ? 'Private' : 'Public'}</p>
                <p className="text-xs text-muted-foreground">{isPrivate ? 'Only invited members can join' : 'Anyone can discover and join'}</p>
              </div>
            </div>
            <div className={`w-10 h-5.5 rounded-full transition-all duration-200 flex items-center px-0.5 ${isPrivate ? 'bg-amber-500' : 'bg-muted'}`}
              style={{ height: 22, width: 40 }}
            >
              <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${isPrivate ? 'translate-x-4' : 'translate-x-0'}`} />
            </div>
          </div>
        </div>

        <div className="px-5 pb-5 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-muted-foreground border border-border hover:bg-muted transition-all">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !name.trim()}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white gradient-blue hover:opacity-90 transition-all disabled:opacity-50"
            style={{ boxShadow: '0 2px 12px rgba(59,110,245,0.35)' }}
          >
            {submitting ? 'Creating...' : 'Create Community'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CommunityGroupsContent() {
  const { user } = useAuth();
  const [communities, setCommunities] = useState<Community[]>([]);
  const [memberships, setMemberships] = useState<MembershipMap>({});
  const [adminCommunities, setAdminCommunities] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [showCreate, setShowCreate] = useState(false);
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const [activityMap, setActivityMap] = useState<ActivityMap>({});
  const [lastVisited, setLastVisitedState] = useState<Record<string, string>>({});

  const fetchCommunities = useCallback(async () => {
    setLoading(true);
    const supabase = createClient();
    const { data } = await supabase
      .from('communities')
      .select('*')
      .order('member_count', { ascending: false });
    setCommunities(data ?? []);
    setLoading(false);
  }, []);

  const fetchMemberships = useCallback(async () => {
    if (!user) { setMemberships({}); setAdminCommunities(new Set()); return; }
    const supabase = createClient();
    const { data } = await supabase
      .from('community_members')
      .select('community_id, role')
      .eq('user_id', user.id);
    const map: MembershipMap = {};
    const adminSet = new Set<string>();
    (data ?? []).forEach((m) => {
      map[m.community_id] = true;
      if (m.role === 'admin') adminSet.add(m.community_id);
    });
    setMemberships(map);
    setAdminCommunities(adminSet);
  }, [user]);

  // Fetch latest activity timestamp for each joined community
  const fetchActivity = useCallback(async (joinedIds: string[]) => {
    if (!joinedIds.length) return;
    const supabase = createClient();

    // Get latest post per community
    const { data: posts } = await supabase
      .from('group_posts')
      .select('community_id, created_at')
      .in('community_id', joinedIds)
      .order('created_at', { ascending: false });

    // Get latest comment per community (via group_posts join)
    const { data: comments } = await supabase
      .from('group_post_comments')
      .select('created_at, group_posts!inner(community_id)')
      .in('group_posts.community_id', joinedIds)
      .order('created_at', { ascending: false });

    // Get latest like per community (via group_posts join)
    const { data: likes } = await supabase
      .from('group_post_likes')
      .select('created_at, group_posts!inner(community_id)')
      .in('group_posts.community_id', joinedIds)
      .order('created_at', { ascending: false });

    const map: ActivityMap = {};

    // Initialize with nulls
    joinedIds.forEach((id) => { map[id] = null; });

    // Process posts
    (posts ?? []).forEach((p) => {
      const cid = p.community_id;
      if (!map[cid] || p.created_at > map[cid]!) map[cid] = p.created_at;
    });

    // Process comments
    (comments ?? []).forEach((c: any) => {
      const cid = c.group_posts?.community_id;
      if (!cid) return;
      if (!map[cid] || c.created_at > map[cid]!) map[cid] = c.created_at;
    });

    // Process likes
    (likes ?? []).forEach((l: any) => {
      const cid = l.group_posts?.community_id;
      if (!cid) return;
      if (!map[cid] || l.created_at > map[cid]!) map[cid] = l.created_at;
    });

    setActivityMap(map);
  }, []);

  useEffect(() => {
    fetchCommunities();
  }, [fetchCommunities]);

  useEffect(() => {
    fetchMemberships();
  }, [fetchMemberships]);

  // Load last-visited from localStorage (client only)
  useEffect(() => {
    setLastVisitedState(getLastVisited());
  }, []);

  // Once memberships are loaded, fetch activity for joined groups
  useEffect(() => {
    const joinedIds = Object.keys(memberships).filter((id) => memberships[id]);
    if (joinedIds.length > 0) {
      fetchActivity(joinedIds);
    }
  }, [memberships, fetchActivity]);

  // Determine if a community has new activity since last visit
  const hasNewActivity = useCallback((communityId: string): boolean => {
    const latestActivity = activityMap[communityId];
    if (!latestActivity) return false;
    const lastSeen = lastVisited[communityId];
    if (!lastSeen) return true; // never visited → treat as new
    return latestActivity > lastSeen;
  }, [activityMap, lastVisited]);

  const handleMarkVisited = useCallback((communityId: string) => {
    setLastVisited(communityId);
    setLastVisitedState((prev) => ({ ...prev, [communityId]: new Date().toISOString() }));
  }, []);

  const handleJoin = async (communityId: string) => {
    if (!user) { toast.error('Please sign in to join communities'); return; }
    setJoiningId(communityId);
    const supabase = createClient();
    const { error } = await supabase.from('community_members').insert({
      community_id: communityId,
      user_id: user.id,
      role: 'member',
    });
    if (error) {
      toast.error('Failed to join community');
    } else {
      toast.success('Joined community!');
      setMemberships((prev) => ({ ...prev, [communityId]: true }));
      setCommunities((prev) =>
        prev.map((c) => c.id === communityId ? { ...c, member_count: c.member_count + 1 } : c)
      );
    }
    setJoiningId(null);
  };

  const handleLeave = async (communityId: string) => {
    if (!user) return;
    setJoiningId(communityId);
    const supabase = createClient();
    const { error } = await supabase
      .from('community_members')
      .delete()
      .eq('community_id', communityId)
      .eq('user_id', user.id);
    if (error) {
      toast.error('Failed to leave community');
    } else {
      toast.success('Left community');
      setMemberships((prev) => { const n = { ...prev }; delete n[communityId]; return n; });
      setCommunities((prev) =>
        prev.map((c) => c.id === communityId ? { ...c, member_count: Math.max(0, c.member_count - 1) } : c)
      );
    }
    setJoiningId(null);
  };

  const filtered = communities.filter((c) => {
    const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.description?.toLowerCase().includes(search.toLowerCase());
    const matchCat = activeCategory === 'All' || c.category === activeCategory;
    return matchSearch && matchCat;
  });

  // Sort joined communities: those with new activity first, then by member_count
  const myCommunities = filtered
    .filter((c) => memberships[c.id])
    .sort((a, b) => {
      const aNew = hasNewActivity(a.id) ? 1 : 0;
      const bNew = hasNewActivity(b.id) ? 1 : 0;
      if (bNew !== aNew) return bNew - aNew; // new activity first
      // Secondary sort: latest activity timestamp desc
      const aTs = activityMap[a.id] ?? a.created_at;
      const bTs = activityMap[b.id] ?? b.created_at;
      return bTs > aTs ? 1 : bTs < aTs ? -1 : 0;
    });

  const discoverCommunities = filtered.filter((c) => !memberships[c.id]);

  const newActivityCount = myCommunities.filter((c) => hasNewActivity(c.id)).length;

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Community Groups</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Discover, join, and create communities</p>
        </div>
        {user && (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white gradient-blue hover:opacity-90 transition-all"
            style={{ boxShadow: '0 2px 12px rgba(59,110,245,0.35)' }}
          >
            <PlusIcon style={{ width: 16, height: 16 }} />
            <span className="hidden sm:inline">Create</span>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" style={{ width: 16, height: 16 }} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search communities..."
          className="w-full bg-card border border-border rounded-xl pl-9 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
        />
      </div>

      {/* Category filter */}
      <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
              activeCategory === cat
                ? 'bg-primary text-white shadow-sm'
                : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* My Communities */}
          {myCommunities.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground/60">My Communities</p>
                {adminCommunities.size > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/20">
                    {adminCommunities.size} admin
                  </span>
                )}
                {newActivityCount > 0 && (
                  <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/20 animate-pulse">
                    <BellIcon style={{ width: 10, height: 10 }} />
                    {newActivityCount} new
                  </span>
                )}
              </div>
              <div className="space-y-2.5">
                {myCommunities.map((community) => (
                  <CommunityCard
                    key={community.id}
                    community={community}
                    isMember={true}
                    isAdmin={adminCommunities.has(community.id)}
                    isJoining={joiningId === community.id}
                    hasNewActivity={hasNewActivity(community.id)}
                    onJoin={handleJoin}
                    onLeave={handleLeave}
                    onVisit={handleMarkVisited}
                    isGuest={!user}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Discover */}
          {discoverCommunities.length > 0 && (
            <section>
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground/60 mb-3">
                {myCommunities.length > 0 ? 'Discover More' : 'All Communities'}
              </p>
              <div className="space-y-2.5">
                {discoverCommunities.map((community) => (
                  <CommunityCard
                    key={community.id}
                    community={community}
                    isMember={false}
                    isAdmin={false}
                    isJoining={joiningId === community.id}
                    hasNewActivity={false}
                    onJoin={handleJoin}
                    onLeave={handleLeave}
                    onVisit={handleMarkVisited}
                    isGuest={!user}
                  />
                ))}
              </div>
            </section>
          )}

          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
                <UserGroupIcon className="text-muted-foreground" style={{ width: 28, height: 28 }} />
              </div>
              <p className="text-sm font-semibold text-foreground mb-1">No communities found</p>
              <p className="text-xs text-muted-foreground mb-4">
                {search ? 'Try a different search term' : 'Be the first to create one!'}
              </p>
              {user && (
                <button
                  onClick={() => setShowCreate(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white gradient-blue hover:opacity-90 transition-all"
                  style={{ boxShadow: '0 2px 12px rgba(59,110,245,0.35)' }}
                >
                  <PlusIcon style={{ width: 16, height: 16 }} />
                  Create Community
                </button>
              )}
            </div>
          )}
        </>
      )}

      {showCreate && (
        <CreateCommunityModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { fetchCommunities(); fetchMemberships(); }}
        />
      )}
    </div>
  );
}

function CommunityCard({
  community,
  isMember,
  isAdmin,
  isJoining,
  hasNewActivity,
  onJoin,
  onLeave,
  onVisit,
  isGuest,
}: {
  community: Community;
  isMember: boolean;
  isAdmin: boolean;
  isJoining: boolean;
  hasNewActivity: boolean;
  onJoin: (id: string) => void;
  onLeave: (id: string) => void;
  onVisit: (id: string) => void;
  isGuest?: boolean;
}) {
  const [failedProfileUrl, setFailedProfileUrl] = useState<string | null>(null);
  const catColor = CATEGORY_COLORS[community.category] ?? 'bg-slate-500/20 text-slate-400';

  return (
    <Link
      href={`/community-groups/${community.id}`}
      onClick={() => onVisit(community.id)}
      className={`block bg-card border rounded-xl p-3 hover:border-primary/30 hover:bg-card/80 transition-all duration-200 group cursor-pointer ${
        hasNewActivity
          ? 'border-primary/40 shadow-sm'
          : 'border-border'
      }`}
      style={hasNewActivity ? { boxShadow: '0 0 0 1px rgba(59,110,245,0.15), 0 2px 8px rgba(59,110,245,0.08)' } : undefined}
    >
      <div className="flex items-center gap-3">
        {/* Avatar with activity ring */}
        <div className="relative flex-shrink-0">
          <div
            className={`w-10 h-10 rounded-xl overflow-hidden ${hasNewActivity ? 'ring-2 ring-primary/60 ring-offset-1 ring-offset-card' : ''}`}
            style={!hasNewActivity ? { boxShadow: '0 2px 8px rgba(59,110,245,0.25)' } : undefined}
          >
            {community.profile_photo_url && community.profile_photo_url !== failedProfileUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img onError={() => setFailedProfileUrl(community.profile_photo_url ?? null)} src={community.profile_photo_url} alt={`${community.name} group photo`} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full gradient-blue flex items-center justify-center text-white font-bold text-sm">
                {community.name.slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          {/* New activity dot */}
          {hasNewActivity && (
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-primary border-2 border-card animate-pulse" />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-sm font-bold truncate transition-colors ${hasNewActivity ? 'text-primary' : 'text-foreground group-hover:text-primary'}`}>
              {community.name}
            </span>
            {community.is_private ? (
              <LockClosedIcon className="text-amber-400 flex-shrink-0" style={{ width: 11, height: 11 }} />
            ) : (
              <GlobeAltIcon className="text-green-400 flex-shrink-0" style={{ width: 11, height: 11 }} />
            )}
            {hasNewActivity && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primary/15 text-primary flex-shrink-0">
                New activity
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${catColor}`}>
              {community.category}
            </span>
            {isAdmin && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 flex-shrink-0">
                👑 Admin
              </span>
            )}
            <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <UsersIcon style={{ width: 11, height: 11 }} />
              {community.member_count.toLocaleString()}
            </span>
            {community.description && (
              <span className="text-[11px] text-muted-foreground truncate hidden sm:inline">
                · {community.description.slice(0, 50)}{community.description.length > 50 ? '…' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.preventDefault()}>
          {isMember && !isAdmin ? (
            <button
              onClick={(e) => { e.preventDefault(); onLeave(community.id); }}
              disabled={isJoining}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-semibold text-muted-foreground border border-border hover:bg-muted transition-all disabled:opacity-50"
            >
              <CheckIcon style={{ width: 11, height: 11 }} />
              Joined
            </button>
          ) : !isMember ? (
            <button
              onClick={(e) => { e.preventDefault(); if (!isGuest) onJoin(community.id); }}
              disabled={isJoining || isGuest}
              title={isGuest ? 'Sign in to join communities' : undefined}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-bold text-white gradient-blue hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ boxShadow: '0 2px 8px rgba(59,110,245,0.3)' }}
            >
              <PlusIcon style={{ width: 11, height: 11 }} />
              Join
            </button>
          ) : null}
          <ChevronRightIcon className={`transition-colors ${hasNewActivity ? 'text-primary' : 'text-muted-foreground group-hover:text-primary'}`} style={{ width: 15, height: 15 }} />
        </div>
      </div>
    </Link>
  );
}
